import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

/**
 * Modal component with ArbiWin design system integration
 * Supports backdrop clicks, focus management, and keyboard navigation
 *
 * @param {Object} props - Modal properties
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.size - Modal size: 'small', 'medium', 'large', 'fullscreen'
 * @param {boolean} props.closeOnBackdrop - Close on backdrop click
 * @param {boolean} props.closeOnEscape - Close on escape key
 * @param {boolean} props.showCloseButton - Show close button
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {Function} props.onAfterOpen - Callback after modal opens
 * @param {Function} props.onAfterClose - Callback after modal closes
 * @returns {JSX.Element|null} Modal component
 */
const Modal = function Modal({
  isOpen = false,
  onClose,
  size = 'medium',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  title,
  children,
  className = '',
  testId,
  onAfterOpen,
  onAfterClose
}) {

  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const isInitialRender = useRef(true);

  // Focus management
  const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const getFocusableElements = useCallback(function() {
    if (!modalRef.current) return [];
    return Array.from(modalRef.current.querySelectorAll(focusableElements));
  }, []);

  const trapFocus = useCallback(function(event) {
    const focusableEls = getFocusableElements();
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          event.preventDefault();
        }
      }
    }
  }, [getFocusableElements]);

  // Handle escape key
  const handleEscapeKey = useCallback(function(event) {
    if (event.key === 'Escape' && closeOnEscape && onClose) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(function(event) {
    if (event.target === event.currentTarget && closeOnBackdrop && onClose) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);

  // Handle close button click
  const handleCloseClick = useCallback(function() {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Effect for opening modal
  useEffect(function() {
    if (isOpen) {
      // Store previous focus
      previousFocusRef.current = document.activeElement;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Add event listeners
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('keydown', trapFocus);

      // Focus first focusable element
      setTimeout(function() {
        const focusableEls = getFocusableElements();
        if (focusableEls.length > 0) {
          focusableEls[0].focus();
        } else if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);

      // Call onAfterOpen
      if (onAfterOpen && !isInitialRender.current) {
        onAfterOpen();
      }
      isInitialRender.current = false;
    } else {
      // Restore body scroll
      document.body.style.overflow = '';

      // Remove event listeners
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('keydown', trapFocus);

      // Restore previous focus
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }

      // Call onAfterClose
      if (onAfterClose && !isInitialRender.current) {
        onAfterClose();
      }
    }

    // Cleanup on unmount
    return function() {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('keydown', trapFocus);
    };
  }, [isOpen, handleEscapeKey, trapFocus, getFocusableElements, onAfterOpen, onAfterClose]);

  if (!isOpen) {
    return null;
  }

  const modalClasses = [
    'arbiwin-modal',
    'arbiwin-modal--' + size,
    className
  ].filter(Boolean).join(' ');

  const modalContent = (
    <div
      className="arbiwin-modal-backdrop"
      onClick={handleBackdropClick}
      data-testid={testId ? testId + '-backdrop' : 'arbiwin-modal-backdrop'}
    >
      <div
        ref={modalRef}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'arbiwin-modal-title' : undefined}
        tabIndex={-1}
        data-testid={testId || 'arbiwin-modal'}
      >
        {(title || showCloseButton) && (
          <div className="arbiwin-modal__header">
            {title && (
              <h2
                id="arbiwin-modal-title"
                className="arbiwin-modal__title"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="arbiwin-modal__close-button"
                onClick={handleCloseClick}
                aria-label="Затвори диалог"
                data-testid={testId ? testId + '-close' : 'arbiwin-modal-close'}
              >
                <svg
                  className="arbiwin-modal__close-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="arbiwin-modal__content">
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal in portal
  return createPortal(modalContent, document.body);
};

Modal.displayName = 'Modal';

export default Modal;
